import {
  projectFertilityOutcomes,
  PROJECTION_MODEL_ID,
  PROJECTION_MODEL_VERSION,
  selectProjectionModel,
} from "./model";
import {
  applySourceBackedModifiers,
  type AppliedProjectionModifier,
} from "./modifiers";
import type {
  FertilityPredictionResult,
  FertilityPredictionSnapshot,
  FertilityProjectionResult,
  ObservedValue,
  ProjectionChange,
  ProjectionEvidenceUsage,
  ProjectionGoal,
  ProjectionInput,
  ProjectionModelTrace,
  ProjectionStage,
  ProjectionTargetResult,
} from "./types";

export const DEFAULT_PROJECTION_GOAL: ProjectionGoal = {
  kind: "probability_at_least_one_live_birth",
};

type OrchestratorOptions = {
  modelId?: string;
  now?: Date;
  previous?: FertilityPredictionSnapshot;
};

/**
 * Shared entry point for every prediction and funnel.
 *
 * The underlying clinical models remain replaceable. This layer is
 * responsible for selecting the requested target, preserving observations,
 * recording which inputs were applied, and making unsupported inputs visible
 * instead of silently applying an unvalidated multiplier.
 */
export function projectFertilityPrediction(
  input: ProjectionInput,
  options: OrchestratorOptions = {},
): FertilityPredictionResult {
  const goal = normalizeGoal(input.goal, input.kind);
  const selectedModel = selectProjectionModel(input, goal);
  const baseProjection = projectFertilityOutcomes(input, {
    modelId: options.modelId ?? selectedModel.metadata.id,
    now: options.now,
  });
  const modifierResult = applySourceBackedModifiers(input, baseProjection);
  const projection = modifierResult.projection;
  const evidenceUsage = collectEvidenceUsage(
    input,
    projection,
    modifierResult.applications,
  );
  const target = evaluateTarget(goal, projection);
  const modelTrace = buildModelTrace(
    input,
    projection,
    evidenceUsage,
    modifierResult.applications,
  );
  const changes = compareWithPrevious(
    goal,
    target,
    evidenceUsage,
    options.previous,
  );

  return {
    ...projection,
    goal,
    target,
    evidenceUsage,
    modelTrace,
    changes,
  };
}

export function createFertilityPredictionSnapshot(
  id: string,
  input: ProjectionInput,
  options: OrchestratorOptions = {},
): FertilityPredictionSnapshot {
  const now = options.now ?? new Date();
  const result = projectFertilityPrediction(input, { ...options, now });

  return {
    id,
    createdAt: now.toISOString(),
    input: { ...input, goal: result.goal },
    result,
  };
}

function normalizeGoal(
  goal: ProjectionGoal | undefined,
  kind?: ProjectionInput["kind"],
): ProjectionGoal {
  if (!goal) {
    return kind === "frozen_semen_cohort"
      ? {
          kind: "expected_stage_count",
          stage: "post_thaw_motility_recovery_percent",
        }
      : DEFAULT_PROJECTION_GOAL;
  }
  if (goal.kind === "probability_at_least_n_live_births") {
    return {
      kind: goal.kind,
      count: Math.max(1, Math.round(goal.count)),
    };
  }
  if (goal.kind === "probability_at_least_stage_count") {
    return {
      kind: goal.kind,
      stage: goal.stage,
      count: Math.max(1, Math.round(goal.count)),
    };
  }
  return goal;
}

function evaluateTarget(
  goal: ProjectionGoal,
  projection: FertilityProjectionResult,
): ProjectionTargetResult {
  switch (goal.kind) {
    case "probability_at_least_one_live_birth":
      return {
        goal,
        estimate: projection.probabilityAtLeastOneLiveBirth,
        basis: projection.probabilityAtLeastOneLiveBirth
          ? "modeled"
          : "unavailable",
        available: projection.probabilityAtLeastOneLiveBirth != null,
        reason:
          projection.probabilityAtLeastOneLiveBirth == null
            ? "A calibrated live-birth probability is not available for the supplied inputs."
            : undefined,
      };
    case "expected_stage_count":
      return expectedStageTarget(goal, projection.stages);
    case "probability_at_least_n_live_births":
      if (goal.count === 1) {
        return {
          goal: { kind: "probability_at_least_one_live_birth" },
          estimate: projection.probabilityAtLeastOneLiveBirth,
          basis: projection.probabilityAtLeastOneLiveBirth
            ? "modeled"
            : "unavailable",
          available: projection.probabilityAtLeastOneLiveBirth != null,
          reason:
            projection.probabilityAtLeastOneLiveBirth == null
              ? "A calibrated live-birth probability is not available for the supplied inputs."
              : undefined,
        };
      }
      return {
        goal,
        estimate: projection.probabilityAtLeastNLiveBirths ?? null,
        basis: projection.probabilityAtLeastNLiveBirths
          ? "modeled"
          : "unavailable",
        available: projection.probabilityAtLeastNLiveBirths != null,
        reason: projection.probabilityAtLeastNLiveBirths
          ? undefined
          : "No registered model currently exposes a calibrated distribution for multiple live births; this must not be inferred from an average stage rate.",
      };
    case "probability_at_least_stage_count":
      return {
        goal,
        estimate: null,
        basis: "unavailable",
        available: false,
        reason:
          "No registered model currently exposes a calibrated attainment distribution for this stage; an expected count cannot be converted into a target probability without one.",
      };
  }
}

function expectedStageTarget(
  goal: Extract<ProjectionGoal, { kind: "expected_stage_count" }>,
  stages: ProjectionStage[],
): ProjectionTargetResult {
  const stage = stages.find((candidate) => candidate.id === goal.stage);
  if (!stage) {
    return {
      goal,
      estimate: null,
      basis: "unavailable",
      available: false,
      reason: `The ${goal.stage} stage is not available from the supplied observations or model.`,
    };
  }
  return {
    goal,
    estimate: stage.estimate,
    basis: stage.basis,
    available: true,
  };
}

function collectEvidenceUsage(
  input: ProjectionInput,
  projection: FertilityProjectionResult,
  applications: AppliedProjectionModifier[],
): ProjectionEvidenceUsage[] {
  const usage: ProjectionEvidenceUsage[] = [];
  const add = (
    id: string,
    label: string,
    observed: ObservedValue<unknown> | undefined,
    status: ProjectionEvidenceUsage["status"],
    reason: string,
    modelIds: string[] = [],
  ) => {
    if (!observed) return;
    usage.push({
      id,
      label,
      status,
      modelIds,
      sourceIds: observed.provenance.sourceId
        ? [observed.provenance.sourceId]
        : [],
      reason,
    });
  };

  const appliedModelIds = [projection.model.id];
  const modelProducedEstimate =
    projection.probabilityAtLeastOneLiveBirth != null ||
    projection.stages.some((stage) => stage.basis === "modeled");
  const retrievalApplied =
    projection.model.id === "moon-oocyte-yield-2016-oocyte-yield" &&
    projection.stages.some((stage) =>
      stage.sourceIds.includes("moon-oocyte-yield-2016"),
    );
  const age = input.patient.ageAtRetrieval ?? input.patient.ageAtTransfer;
  if (age) {
    const ageApplied =
      input.kind !== "observed_funnel" &&
      projection.model.supportedFeatureIds.includes("age") &&
      modelProducedEstimate &&
      !projection.missingInputs.includes(
        "patient age at the relevant treatment step",
      );
    add(
      "patient.age",
      "Patient age",
      age,
      ageApplied ? "applied" : "recorded_not_applied",
      ageApplied
        ? "Age selected the applicable age band for the current outcome model."
        : "Age was recorded but is not used by the selected funnel or without a reviewed rate set.",
      ageApplied ? appliedModelIds : [],
    );
  }

  addUnsupported(
    usage,
    "patient.amh",
    "AMH",
    input.patient.amhNgMl,
    "No validated retrieval-response model is registered in this projection pipeline.",
    retrievalApplied && projection.model.supportedFeatureIds.includes("amh")
      ? projection.model.id
      : undefined,
  );
  addUnsupported(
    usage,
    "patient.afc",
    "Antral follicle count",
    input.patient.antralFollicleCount,
    "No validated retrieval-response model is registered in this projection pipeline.",
    retrievalApplied &&
      projection.model.supportedFeatureIds.includes("antral_follicle_count")
      ? projection.model.id
      : undefined,
  );
  addUnsupported(
    usage,
    "patient.fsh",
    "FSH",
    input.patient.fshIuL,
    "No validated retrieval-response model is registered in this projection pipeline.",
    retrievalApplied && projection.model.supportedFeatureIds.includes("fsh")
      ? projection.model.id
      : undefined,
  );
  addUnsupported(
    usage,
    "patient.medical_factors",
    "Medical factors",
    input.patient.medicalFactors,
    "No validated disease-specific modifier is registered for this target and population.",
    applications
      .filter((application) =>
        application.inputIds.includes("patient.medical_factors"),
      )
      .map((application) => application.modelId),
  );

  input.semen?.forEach((sample, index) => {
    const sampleFields = [
      ["volume_ml", sample.volumeMl],
      ["concentration", sample.concentrationMillionPerMl],
      ["total_motility", sample.totalMotilityPercent],
      ["progressive_motility", sample.progressiveMotilityPercent],
      ["viability", sample.viabilityPercent],
      ["morphology", sample.morphologyPercent],
      ["dna_fragmentation", sample.dnaFragmentationPercent],
      [
        "post_thaw_progressive_motility",
        sample.postThawProgressiveMotilityPercent,
      ],
      ["post_thaw_viability", sample.postThawViabilityPercent],
      ["source", sample.source],
      ["fertilization_method", sample.fertilizationMethod],
    ] as const;
    sampleFields.forEach(([field, observed]) => {
      const reason =
        field === "volume_ml"
          ? "Volume is retained as laboratory context but is not used by the cryosurvival baseline."
          : field === "post_thaw_progressive_motility" ||
              field === "post_thaw_viability"
            ? "The observed post-thaw laboratory value is represented directly in the shared projection stages."
            : field === "dna_fragmentation"
              ? "The 2024 umbrella review found weak or non-significant evidence linking sperm DNA fragmentation with ART live birth, so it is retained without a multiplier."
              : field === "fertilization_method"
                ? "The method selects the applicable semen endpoint but is not itself an outcome multiplier."
                : "The selected sperm cryosurvival adapter uses this measurement as source-backed freezability context.";
      const spermModelApplied =
        projection.model.id === "marchiani-2023-general-semen-cryosurvival" &&
        [
          "concentration",
          "total_motility",
          "progressive_motility",
          "viability",
          "source",
          "post_thaw_progressive_motility",
          "post_thaw_viability",
        ].includes(field);
      addUnsupported(
        usage,
        `semen.${index}.${field}`,
        `Semen ${field.replaceAll("_", " ")}`,
        observed,
        reason,
        spermModelApplied
          ? projection.model.id
          : applications.find((application) =>
              application.inputIds.includes(`semen.${index}.${field}`),
            )?.modelId,
      );
    });
  });

  collectObservedStageUsage(usage, input, projection);

  input.funnel?.stages.forEach((funnelStage) => {
    if (!funnelStage.value) return;
    const stage = projection.stages.find(
      (candidate) => candidate.id === funnelStage.id,
    );
    const applied = stage?.basis === "observed";
    const evidenceId =
      funnelStage.id === "retrieved_oocytes"
        ? "cycle.retrieved"
        : funnelStage.id === "mature_oocytes"
          ? "cycle.mature"
          : `funnel.${funnelStage.id}`;
    usage.push({
      id: evidenceId,
      label: funnelStage.id,
      status: applied ? "applied" : "recorded_not_applied",
      modelIds: applied ? [projection.model.id] : [],
      sourceIds: funnelStage.value.provenance.sourceId
        ? [funnelStage.value.provenance.sourceId]
        : [],
      reason: applied
        ? "The funnel observation is represented directly in the shared projection stages."
        : "The funnel observation was supplied but could not be represented by the selected model.",
    });
  });

  input.evidence?.forEach((source) => {
    const isRateSource = projection.stages.some((stage) =>
      stage.rate?.sourceIds.includes(source.id),
    );
    usage.push({
      id: `evidence.${source.id}`,
      label: source.label,
      status: isRateSource ? "applied" : "recorded_not_applied",
      modelIds: isRateSource ? appliedModelIds : [],
      sourceIds: [source.id],
      reason: isRateSource
        ? "The selected rate set identified this source as a parameter source."
        : "The source was supplied as context but did not determine a parameter in the selected model.",
    });
  });

  return usage;
}

function addUnsupported(
  usage: ProjectionEvidenceUsage[],
  id: string,
  label: string,
  observed: ObservedValue<unknown> | undefined,
  reason: string,
  supportedModelId?: string | string[],
): void {
  if (!observed) return;
  const modelIds = supportedModelId
    ? Array.isArray(supportedModelId)
      ? supportedModelId
      : [supportedModelId]
    : [];
  usage.push({
    id,
    label,
    status: modelIds.length > 0 ? "applied" : "recorded_not_applied",
    modelIds,
    sourceIds: observed.provenance.sourceId
      ? [observed.provenance.sourceId]
      : [],
    reason:
      modelIds.length > 0
        ? "The selected model declares this feature as an applicable input."
        : reason,
  });
}

function collectObservedStageUsage(
  usage: ProjectionEvidenceUsage[],
  input: ProjectionInput,
  projection: FertilityProjectionResult,
): void {
  const stages: [string, string, ObservedValue<number> | undefined, string][] =
    [
      [
        "oocytes.frozen",
        "Frozen oocytes",
        input.oocytes?.frozen,
        "frozen_oocytes",
      ],
      [
        "oocytes.thawed",
        "Thawed oocytes",
        input.oocytes?.thawed,
        "thawed_oocytes",
      ],
      [
        "oocytes.fertilized",
        "Fertilized oocytes",
        input.oocytes?.fertilized,
        "fertilized_oocytes",
      ],
      [
        "oocytes.blastocysts",
        "Blastocysts",
        input.oocytes?.blastocysts,
        "blastocysts",
      ],
      [
        "embryos.frozen",
        "Frozen embryos",
        input.embryos?.frozen,
        "frozen_embryos",
      ],
      [
        "embryos.warmed",
        "Warmed embryos",
        input.embryos?.warmed,
        "warmed_embryos",
      ],
      [
        "embryos.transferred",
        "Transferred embryos",
        input.embryos?.transferred,
        "transferred_embryos",
      ],
      [
        "cycle.retrieved",
        "Retrieved oocytes",
        input.cycle?.retrievedOocytes,
        "retrieved_oocytes",
      ],
      [
        "cycle.expected_retrieved",
        "Expected retrieved oocytes",
        input.cycle?.expectedRetrievedOocytes,
        "retrieved_oocytes",
      ],
      [
        "cycle.mature",
        "Mature oocytes",
        input.cycle?.matureOocytes,
        "mature_oocytes",
      ],
      [
        "cycle.vitrified",
        "Vitrified oocytes",
        input.cycle?.vitrifiedOocytes,
        "vitrified_oocytes",
      ],
      [
        "semen.samples",
        "Collected semen samples",
        input.semenInventory?.collectedSamples,
        "semen_samples",
      ],
      [
        "semen.processed",
        "Processed semen samples",
        input.semenInventory?.processedSamples,
        "processed_semen",
      ],
      [
        "semen.frozen_vials",
        "Frozen semen vials",
        input.semenInventory?.frozenVials,
        "frozen_vials",
      ],
    ];

  stages.forEach(([id, label, observed, stageId]) => {
    if (!observed) return;
    const stage = projection.stages.find(
      (candidate) => candidate.id === stageId,
    );
    const applied =
      stage?.basis === "observed" || stage?.basis === "provided_projection";
    usage.push({
      id,
      label,
      status: applied ? "applied" : "recorded_not_applied",
      modelIds: applied ? [projection.model.id] : [],
      sourceIds: observed.provenance.sourceId
        ? [observed.provenance.sourceId]
        : [],
      reason: applied
        ? "The observed count is represented directly in the projection stages."
        : "The observed count was supplied but could not be placed in the selected model stages.",
    });
  });
}

function buildModelTrace(
  input: ProjectionInput,
  projection: FertilityProjectionResult,
  evidenceUsage: ProjectionEvidenceUsage[],
  applications: AppliedProjectionModifier[],
): ProjectionModelTrace[] {
  const appliedInputs = evidenceUsage
    .filter((item) => item.status === "applied")
    .map((item) => item.id);
  const modelApplied =
    projection.stages.length > 0 ||
    projection.probabilityAtLeastOneLiveBirth != null;
  const trace: ProjectionModelTrace[] = [
    {
      modelId: projection.model.id,
      version: projection.model.version,
      role: input.kind === "observed_funnel" ? "funnel" : "outcome",
      status: modelApplied ? "applied" : "recorded_not_applied",
      inputIds: appliedInputs,
      sourceIds: projection.model.parameterSourceIds,
      reason: modelApplied
        ? input.kind === "observed_funnel"
          ? "The shared funnel model preserved observed stages and applied only explicitly supplied transition rates."
          : "The registered projection model produced the available outcome stages."
        : "The registered model could not produce a stage or target estimate from the supplied inputs.",
    },
  ];

  applications.forEach((application) => {
    trace.push({
      modelId: application.modelId,
      version: "1.0.0",
      role: "modifier",
      status: "applied",
      inputIds: application.inputIds,
      sourceIds: application.sourceIds,
      reason: application.reason,
    });
  });

  const unsupported = evidenceUsage.filter(
    (item) => item.status === "recorded_not_applied",
  );
  if (unsupported.length > 0) {
    trace.push({
      modelId: `${PROJECTION_MODEL_ID}:unsupported-inputs`,
      version: PROJECTION_MODEL_VERSION,
      role: "modifier",
      status: "recorded_not_applied",
      inputIds: unsupported.map((item) => item.id),
      sourceIds: unsupported.flatMap((item) => item.sourceIds),
      reason:
        "These inputs were retained for a future validated adapter and were not converted into unsupported multipliers.",
    });
  }

  return trace;
}

function compareWithPrevious(
  goal: ProjectionGoal,
  target: ProjectionTargetResult,
  evidenceUsage: ProjectionEvidenceUsage[],
  previous: FertilityPredictionSnapshot | undefined,
): ProjectionChange[] {
  if (!previous) return [];
  const changes: ProjectionChange[] = [];
  const previousTarget = previous.result.target;
  const previousGoal = JSON.stringify(previous.result.goal);
  const nextGoal = JSON.stringify(goal);

  if (previousGoal !== nextGoal) {
    changes.push({
      field: "target_goal",
      previousValue: previousGoal,
      nextValue: nextGoal,
      reason: "The requested prediction target changed.",
      sourceIds: [],
    });
  }

  const previousMean = previousTarget.estimate?.mean ?? null;
  const nextMean = target.estimate?.mean ?? null;
  if (previousMean !== nextMean) {
    const delta =
      previousMean != null && nextMean != null
        ? nextMean - previousMean
        : undefined;
    changes.push({
      field: "target_estimate",
      previousValue: previousMean,
      nextValue: nextMean,
      delta,
      direction:
        delta == null || delta === 0
          ? "unchanged"
          : delta > 0
            ? "increase"
            : "decrease",
      reason: "The projection was recomputed from the updated evidence set.",
      sourceIds: evidenceUsage.flatMap((item) => item.sourceIds),
    });
  }

  const previousApplied = new Set(
    previous.result.evidenceUsage
      .filter((item) => item.status === "applied")
      .map((item) => item.id),
  );
  const newlyApplied = evidenceUsage.filter(
    (item) => item.status === "applied" && !previousApplied.has(item.id),
  );
  if (newlyApplied.length > 0) {
    changes.push({
      field: "evidence_usage",
      previousValue: previousApplied.size,
      nextValue: previousApplied.size + newlyApplied.length,
      reason: "New evidence became applicable to the selected model.",
      sourceIds: newlyApplied.flatMap((item) => item.sourceIds),
    });
  }

  return changes;
}
